import React from 'react';
import { supabase } from '../lib/supabase';

const ACCENT = {
  technology:    'border-indigo-300 bg-indigo-50 text-indigo-600',
  finance:       'border-emerald-300 bg-emerald-50 text-emerald-600',
  india_finance: 'border-slate-300 bg-slate-50 text-slate-700',
  fashion:       'border-pink-300 bg-pink-50 text-pink-600',
  sports:        'border-orange-300 bg-orange-50 text-orange-600',
  health:        'border-teal-300 bg-teal-50 text-teal-600',
  education:     'border-blue-300 bg-blue-50 text-blue-600',
  space:         'border-violet-300 bg-violet-50 text-violet-600',
  science:       'border-purple-300 bg-purple-50 text-purple-600',
};

const TOP_BAR = {
  technology:    'bg-gradient-to-r from-indigo-500 to-purple-500',
  finance:       'bg-gradient-to-r from-emerald-500 to-teal-500',
  india_finance: 'bg-gradient-to-r from-slate-400 via-slate-500 to-slate-600',
  fashion:       'bg-gradient-to-r from-pink-500 to-fuchsia-500',
  sports:        'bg-gradient-to-r from-orange-500 to-amber-500',
  health:        'bg-gradient-to-r from-teal-500 to-cyan-500',
  education:     'bg-gradient-to-r from-blue-500 to-indigo-500',
  space:         'bg-gradient-to-r from-violet-600 to-purple-700',
  science:       'bg-gradient-to-r from-purple-500 to-violet-600',
};

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-IN', {
    year: 'numeric', month: 'short', day: 'numeric',
    timeZone: 'Asia/Kolkata',
  });
  const time = d.toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true,
    timeZone: 'Asia/Kolkata',
  });
  return `${date}, ${time} IST`;
}

function excerpt(text, max = 130) {
  if (!text) return null;
  const clean = text.replace(/<[^>]+>/g, '').trim();
  return clean.length > max ? clean.slice(0, max).trimEnd() + '…' : clean;
}

function sourceDomain(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

function recordView(id) {
  supabase.rpc('increment_view_count', { article_id: id }).catch(() => {});
}

export default function ArticleCard({ article, category }) {
  const { id, title, description, source_url, published_at, fetched_at, view_count } = article;
  const displayDate = formatDate(published_at ?? fetched_at);
  const domain = sourceDomain(source_url);
  const bar    = TOP_BAR[category] ?? 'bg-gradient-to-r from-gray-400 to-gray-500';
  const accent = ACCENT[category]  ?? 'border-gray-300 bg-gray-50 text-gray-600';

  return (
    <article className="group bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-gray-200 transition-all duration-300 flex flex-col h-full overflow-hidden">
      {/* Thin category-coloured bar — expands slightly on hover */}
      <div className={`h-1 w-full ${bar} transition-all duration-300 group-hover:h-1.5`} />

      <div className="p-5 flex-1 flex flex-col">
        <h3 className="text-gray-900 font-semibold text-sm leading-snug line-clamp-2 mb-2 group-hover:text-indigo-700 transition-colors">
          {title}
        </h3>
        {excerpt(description) && (
          <p className="text-gray-500 text-xs leading-relaxed line-clamp-3 flex-1">
            {excerpt(description)}
          </p>
        )}
      </div>

      {/* Source attribution */}
      {domain && (
        <div className="px-5 pb-2 flex items-center gap-1.5">
          <svg className="w-3 h-3 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          <a
            href={source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] text-gray-400 hover:text-gray-600 truncate transition-colors"
            title={source_url}
          >
            {domain}
          </a>
        </div>
      )}

      <footer className="px-5 pb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {displayDate
            ? <time className={`text-xs font-medium px-2 py-0.5 rounded-md border ${accent} shrink-0`}>{displayDate}</time>
            : <span />
          }
          {view_count > 0 && (
            <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 shrink-0">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              {view_count}
            </span>
          )}
        </div>
        <a
          href={source_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => recordView(id)}
          className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors shrink-0"
        >
          Read More
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </footer>
    </article>
  );
}
